import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_AiImageCombiner = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/ai-image-combiner',
			name: 'AI Image Combiner — Merge Two Photos Into One Scene',
			description:
				'Combine two photos into one AI-generated scene — pick a preset style or write a custom prompt. Regenerate fresh variations in seconds. Try Lift!',
			url: 'https://liftapp.ai/ai-image-combiner',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/ai-image-combiner#product' },
				{ '@id': 'https://liftapp.ai/ai-image-combiner#howto' },
				{ '@id': 'https://liftapp.ai/ai-image-combiner#benefits' },
				{ '@id': 'https://liftapp.ai/ai-image-combiner#faq' },
				{ '@id': 'https://liftapp.ai/ai-image-combiner#why-lift' },
				{ '@id': 'https://liftapp.ai/ai-image-combiner#explore-tools' },
				{ '@id': 'https://liftapp.ai/ai-image-combiner#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/ai-image-combiner#product',
			name: 'AI Image Combiner: Merge Two Photos Into One Scene',
			description:
				'Upload two photos, pick a preset style or write a prompt, and let AI combine them into one image. Regenerate variations any time',
			url: 'https://liftapp.ai/ai-image-combiner',
			fileFormat: ['image/jpeg', 'image/png', 'image/webp'],
			memoryRequirements: 'Max file size: 20MB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/ai-image-combiner#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload two photos',
					text: 'Select the two source images you want to merge directly from your device',
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Choose a style or prompt',
					text: 'Pick a preset look or type your own instructions to guide the combination',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Generate the result',
					text: 'AI blends both images into one cohesive scene within seconds',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download or Regenerate anytime',
					text: 'Save the merged image to your device, or update the photos or style and tap Regenerate for a new version',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-image-combiner#benefits',
			name: 'Who can benefit from the AI Image Combiner?',
			description:
				"Lift's AI Image Combiner is useful for social media users, couples, online shoppers, content creators, and mobile users who want to merge two photos into one polished scene.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Social media users — Combine two photos into a single eye-catching post in seconds. The original-versus-result format gives you instant share-ready content for any feed.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Couples & long-distance friends — Merge two solo selfies into one shared scene with a single prompt. Recreate moments together no matter how far apart you are.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Online shoppers — Drop in a portrait and a product shot to preview lipstick, accessories, or outfits on yourself. Test multiple looks before deciding what to buy.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Content creators — Produce concept visuals, mockups, and themed posts by combining two source images. The regenerate loop saves hours of manual editing per asset.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Mobile users — Upload, pick a preset, and view the merged result on any device in seconds. The Regenerate button keeps the workflow fluid without restarting the upload.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'AI Image Combiner by Lift',
				description:
					'Example of how Lift AI Image Combiner blends two source images into one cohesive scene.',
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/ai-image-combiner#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is the Lift AI Image Combiner?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift AI Image Combiner is a tool that merges two uploaded photos into one AI-generated scene. Choose a preset style or write a custom prompt, and the result appears in seconds.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does the Lift AI Image Combiner work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Upload two photos, select a style or type your own prompt, and tap Generate. Lift blends both images into a single result that you can download to your device or regenerate with new inputs at any time.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I write my own prompt to combine the photos?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. Alongside the preset styles, the tool accepts-form instructions so you can describe exactly how the two images should be combined.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the results without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Absolutely. All images generated or edited in Lift can be exported without watermark when you're on the Pro plan. Download them in high-res PNG or JPG — ready for social media, print, ads, or e-commerce.",
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-image-combiner#why-lift',
			name: "Why Lift's AI Image Combiner stands out",
			description:
				"Lift's AI Image Combiner pairs professional-grade AI with intuitive controls and cross-platform access.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'Why use Lift AI Image Combiner',
				description:
					'Comparison of Lift AI Image Combiner benefits — high-quality results, intelligent rendering, multi-platform access, and cost savings.',
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-image-combiner#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/ai-image-combiner#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'AI Image Combiner',
					item: 'https://liftapp.ai/ai-image-combiner',
				},
			],
		},
	],
};
